import React, {
  forwardRef,
  KeyboardEvent,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnButton from "@shared/components/SnButton";
import MailComposePopup from "./MailComposePopup";
import { showResizablePopupModal } from "@widgets/CommonModal";
import { showDialog } from "@widgets/CommonDialog";

interface ReportPopupProps {
  dataSource: any[];
  defaultMailAddress?: string;
  modalRef?: any;
}

const ReportPopup = forwardRef(
  ({ dataSource, defaultMailAddress, modalRef }: ReportPopupProps, ref) => {
    const { userInfo, config } = global(); // Login user 정보
    const [mas8025, setMAS8025] = useState<string>("");

    const [columns, setColums] = useState<any>({
      grid1: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: dataSource,
    });

    const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
      grid1: React.createRef<SnGridRef>(),
    });

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    useImperativeHandle(ref, () => ({
      // 26.08.21 ysw 선택된 행이 없으면 [] 반환.
      // (기존에는 focusedRowNode()가 없을 때 .data 접근에서 예외가 났다)
      getSelectedRow: () => {
        const node = gridRefs.current["grid1"]?.current?.focusedRowNode?.();
        return node?.data ? [node.data] : [];
      },
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData("POPUP_REPORT");
          setColums({
            grid1: rtn.grid.grid1,
          });

          setMAS8025(config?.MAS8025?.dc_rmk ?? "");
        } catch (error) {}
      };
      fetchform();
    }, []);

    const onModelUpdated = () => {
      getGridCurrent("grid1").setFocusedCell(0, "tp_report");
    };

    const onRowDoubleClicked = () => {
      modalRef.current?.handleConfirm(true);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        modalRef.current?.handleConfirm(true);
      }
    };

    const onClick = async (value: string) => {
      switch (value) {
        case "Mail":
          if (!getGridCurrent("grid1").focusedRowNode()) {
            await showDialog("alter", "선택된 항목이 없습니다.");
            return;
          }

          const data = getGridCurrent("grid1").focusedRowNode().data;

          const now = new Date();

          const year: string = String(now.getFullYear());
          const month: string = String(now.getMonth() + 1).padStart(2, "0");
          const day: string = String(now.getDate()).padStart(2, "0");

          const hours: string = String(now.getHours()).padStart(2, "0");
          const minutes: string = String(now.getMinutes()).padStart(2, "0");
          const seconds: string = String(now.getSeconds()).padStart(2, "0");
          const yyyyMMddHHmmss = `${year}${month}${day}${hours}${minutes}${seconds}`;

          let fileName = data.nm_report + yyyyMMddHHmmss + ".pdf";
          const component = (
            <MailComposePopup
              defaultMailAddress={defaultMailAddress}
              attachFileName={fileName}
            />
          );
          const result = await showResizablePopupModal({
            title: "메일작성",
            children: component,
            width: "1200px",
            height: "800px",
          });

          if (!result || result === "close") return;
          break;
      }
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onModelUpdated: onModelUpdated,
            onRowDoubleClicked: onRowDoubleClicked,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div
        className="h-[450px]"
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
        onKeyDown={handleKeyDown}
      >
        <div style={{ display: mas8025 === "LUMIR" ? "block" : "none" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px",
            }}
          >
            <SnButton
              text={"메일전송"}
              onClick={() => {
                onClick("Mail");
              }}
            />
          </div>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 0,
          }}
        >
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "multiple",
              isEditable: false,
            }}
            events={grids.grid1.events}
          />
        </div>
      </div>
    );
  }
);

export default ReportPopup;
